import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GbetBanner from "@/components/GbetBanner";
import PoolResultDisplay from "@/components/PoolResultDisplay";
import AdvertBanner from "@/components/AdvertBanner";
import { WHATSAPP_SALES_URL } from "@/lib/sales";
import axios from "axios";
import { Trophy, Calendar, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";

const sortByRecency = (arr) => {
  // Latest updated first, then most recently created, then latest week number
  return [...arr].sort((a, b) => {
    const at = new Date(a.updated_at || a.created_at || 0).getTime();
    const bt = new Date(b.updated_at || b.created_at || 0).getTime();
    if (bt !== at) return bt - at;
    return (b.week_number || 0) - (a.week_number || 0);
  });
};

const PoolResultsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);

  useEffect(() => {
    axios.get(`${API}/posts?category=pool_result`)
      .then(r => setPosts(sortByRecency(r.data || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const orderedPosts = selectedWeek && selectedWeek !== "latest"
    ? posts.filter(p => p.week_number === parseInt(selectedWeek))
    : posts;

  const weekOptions = posts.filter(p => p.week_number).map(p => p.week_number);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div></div>;

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="pool-results-page">
      <SEO title="Pool Results - UK Football Pool Draw Results" description="Latest UK football pool results and draw outcomes. Check score draws and no score draws for every week. Complete pool result history from Week 4 to current." keywords="pool result, UK pool result, this week pool result, football pool result, score draw result, pool draw numbers, Saturday pool result, pool coupon result" path="/pool/results" />
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <AdvertBanner />
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="bg-green-100 text-green-700 mb-3">Pool Results</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Pool Results</h1>
            <p className="text-gray-500 max-w-xl mx-auto mb-6">Latest football pool results — most recent at the top.</p>

            {/* Week Selector */}
            {weekOptions.length > 0 && (
              <div className="flex items-center justify-center gap-3">
                <Calendar className="h-4 w-4 text-gray-500" />
                <Select value={selectedWeek || ""} onValueChange={v => setSelectedWeek(v || null)}>
                  <SelectTrigger className="w-[220px]" data-testid="week-selector">
                    <SelectValue placeholder="Jump to week..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="latest">All Results</SelectItem>
                    {weekOptions.map(w => (
                      <SelectItem key={w} value={String(w)}>Week {w} - UK 2025/2026</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedWeek && (
                  <Button variant="ghost" size="sm" className="text-gray-500 text-xs" onClick={() => setSelectedWeek(null)}>
                    Clear
                  </Button>
                )}
              </div>
            )}
          </div>

          {orderedPosts.length === 0 ? (
            <div className="text-center py-20">
              <Trophy className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">No pool results posted yet. Check back soon!</p>
              <Button onClick={() => window.open(WHATSAPP_SALES_URL, "_blank", "noopener,noreferrer")} className="bg-[#25D366] hover:bg-[#1DAE55] text-white rounded-full px-6" data-testid="empty-buy-btn">
                <MessageCircle className="h-4 w-4 mr-2" /> Buy on WhatsApp
              </Button>
            </div>
          ) : (
            <div className="space-y-6" data-testid="pool-results-list">
              {orderedPosts.map((post, idx) => (
                <Card
                  key={post.post_id}
                  className={`bg-white border-gray-200 overflow-hidden coupon-border-${post.coupon_color || "brown"} shadow-sm`}
                  data-testid={`pool-result-card-${post.week_number || idx}`}
                >
                  <CardContent className="p-0">
                    <PoolResultDisplay post={post} />
                    <div className="border-t border-gray-100 py-4 px-4 flex flex-wrap items-center justify-between gap-3 bg-gray-50">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {post.season && <Badge variant="outline" className="text-xs text-gray-700 border-gray-300">{post.season} Pools</Badge>}
                        <span>
                          Updated {new Date(post.updated_at || post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </div>
                      <Button
                        onClick={() => window.open(WHATSAPP_SALES_URL, "_blank", "noopener,noreferrer")}
                        size="sm"
                        className="bg-[#25D366] hover:bg-[#1DAE55] text-white font-bold rounded-full px-5"
                        data-testid={`result-subscribe-btn-${post.week_number || idx}`}
                      >
                        <MessageCircle className="h-4 w-4 mr-2" /> Buy on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="px-4"><div className="max-w-3xl mx-auto"><GbetBanner /></div></div>
      <Footer />
    </div>
  );
};

export default PoolResultsPage;
